export type Service = (typeof SERVICES_DATA)[number];

export const SERVICES_DATA = [
  {
    slug: "relationship-therapy",
    title: "Relationship Therapy",
    badge: "Core Service",
    format: "Online & In-person",
    duration: "50 Mins",
    units: "12 Sessions",
    overview:
      "Navigate challenges, build stronger connections, and rediscover intimacy with your partner in a safe, guided space. Learn to communicate effectively and resolve deep-rooted conflicts.",
    recommendedFor: [
      "Couples in conflict",
      "Communication breakdown",
      "Rebuilding trust",
      "Pre-marital guidance",
      "Life transitions",
    ],
    imageUrl:
      "https://images.unsplash.com/photo-1499209974431-9dddcece7f88?auto=format&fit=crop&w=1920&q=80",
    themeColor: "bg-[#1D4263]",
    heroEyebrow: "Couples & Relationships",
    heroDescription:
      "Expert guidance for couples looking to improve communication, resolve deep-rooted conflicts, and foster a healthier, lasting relationship.",
    seasonsIntro:
      "Work, family responsibilities, parenting, financial decisions, personal growth, and life changes can all quietly affect communication and connection. Recognising the season you're in is the first step toward finding your way back to each other.",
    situations: [
      "We want to communicate more openly",
      "The same disagreements keep returning",
      "We feel emotionally distant",
      "We are navigating an important life change",
      "We want to rebuild trust gradually",
      "We want to strengthen an already healthy relationship",
    ],
    benefits: [
      {
        title: "Communicate More Clearly",
        description: "Express thoughts and needs without conversations becoming blame-focused.",
      },
      {
        title: "Understand Each Other Better",
        description:
          "Explore the emotions, expectations, and perspectives behind recurring concerns.",
      },
      {
        title: "Navigate Differences Together",
        description:
          "Develop healthier ways to approach disagreements and important decisions.",
      },
      {
        title: "Strengthen Emotional Connection",
        description:
          "Create more space for trust, appreciation, affection, and meaningful conversations.",
      },
    ],
    benefitsClarifier:
      "A counsellor does not decide who is right or wrong. They help both partners participate in a more constructive conversation.",
    soloStart: {
      question: "Can I begin without my partner?",
      answer:
        "Yes. An individual session can help someone understand their emotions, communication patterns, and relationship needs even when their partner is not ready to participate.",
    },
  },
  {
    slug: "anxiety-healing",
    title: "Anxiety Healing",
    badge: "Targeted Care",
    format: "Online Only",
    duration: "50 Mins",
    units: "8 Sessions",
    overview:
      "Break free from the cycle of chronic worry and panic. Our targeted approach helps you understand your triggers, build lasting resilience, and regain control of your nervous system.",
    recommendedFor: [
      "Chronic worry & panic",
      "Social anxiety",
      "Workplace burnout",
      "Overthinking patterns",
      "Nervous system distress",
    ],
    imageUrl:
      "https://images.unsplash.com/photo-1650869221906-9c095d685cb0?auto=format&fit=crop&w=1920&q=80",
    themeColor: "bg-[#155A7D]",
    heroEyebrow: "Individual Wellness",
    heroDescription:
      "Break free from the cycle of chronic worry and panic. Our targeted approach helps you understand your triggers and build lasting resilience.",
    seasonsIntro:
      "Anxiety rarely shows up for no reason. Work pressure, uncertainty, big decisions, and simply carrying too much for too long can all quietly turn into chronic worry. Recognising the pattern is the first step toward loosening its grip.",
    situations: [
      "My mind won't stop racing at night",
      "Small things trigger a big physical reaction",
      "I avoid situations that make me anxious",
      "I feel anxious even when nothing is wrong",
      "I want to understand what's actually triggering this",
      "I want tools I can use in the moment",
    ],
    benefits: [
      {
        title: "Nervous System Regulation",
        description:
          "Practical tools to calm your body's fight-or-flight response in moments of stress.",
      },
      {
        title: "Cognitive Restructuring",
        description: "Identify and reframe the unhelpful thought patterns that fuel your anxiety.",
      },
      {
        title: "Mindful Awareness",
        description:
          "Develop a grounding practice to stay present instead of spiraling into future worries.",
      },
      {
        title: "In-the-Moment Tools",
        description:
          "Simple, practical techniques to interrupt a spiral before it takes hold.",
      },
    ],
    benefitsClarifier:
      "A therapist doesn't tell you to 'just relax.' They help you understand what your anxiety is protecting you from, and build a calmer relationship with it.",
    soloStart: {
      question: "What if I don't know what's causing my anxiety?",
      answer:
        "That's completely normal, and often where the work begins. Many people start therapy without a clear cause in mind - naming it is part of the process, not a prerequisite for it.",
    },
  },
  {
    slug: "trauma-recovery",
    title: "Trauma Recovery",
    badge: "Deep Healing",
    format: "In-person Only",
    duration: "60 Mins",
    units: "16 Sessions",
    overview:
      "A trauma-informed approach to safely processing painful memories and releasing the emotional weight you've been carrying. Move at your own pace in an environment where your boundaries are always respected.",
    recommendedFor: [
      "Past traumatic events",
      "PTSD symptoms",
      "Childhood wounds",
      "Emotional numbness",
      "Intrusive memories",
    ],
    imageUrl:
      "https://images.unsplash.com/photo-1506126613408-eca07ce68773?auto=format&fit=crop&w=1920&q=80",
    themeColor: "bg-[#3188BE]",
    heroEyebrow: "Deep Healing",
    heroDescription:
      "A trauma-informed approach to safely processing painful memories and releasing the emotional weight you've been carrying.",
    seasonsIntro:
      "The effects of a difficult past don't stay in the past - they shape how safe we feel, how we connect, and how we respond under stress today. Recognising how it shows up now is the first step toward releasing its hold.",
    situations: [
      "Certain memories still feel overwhelming",
      "I feel triggered by things that seem small to others",
      "I go numb instead of feeling emotions",
      "I'm ready to process something from my past",
      "I want to feel safe in my own body again",
      "I want to move forward without forgetting who I am",
    ],
    benefits: [
      {
        title: "Safe Processing",
        description:
          "Move at your own pace in an environment where your boundaries are always respected.",
      },
      {
        title: "Somatic Healing",
        description: "Address how trauma is stored in the body, not just the mind.",
      },
      {
        title: "Reclaiming Identity",
        description:
          "Separate your core self from what happened to you, building a future not defined by the past.",
      },
      {
        title: "Restoring Safety",
        description:
          "Rebuild a felt sense of safety in your body and relationships, at a pace you control.",
      },
    ],
    benefitsClarifier:
      "A trauma-informed therapist never pushes you to relive painful memories before you're ready. Your pace and boundaries lead the process.",
    soloStart: {
      question: "Do I need to talk about the details of what happened?",
      answer:
        "No. You share only what feels safe, when it feels safe. Many people begin by simply describing how the past is affecting them today.",
    },
  },
  {
    slug: "depression-support",
    title: "Depression Support",
    badge: "Core Service",
    format: "Online & In-person",
    duration: "50 Mins",
    units: "10 Sessions",
    overview:
      "Overcome persistent sadness, fatigue, and loss of interest. Reconnect with joy, vitality, and meaning through evidence-based cognitive and behavioral approaches.",
    recommendedFor: [
      "Persistent low mood",
      "Lethargy & low energy",
      "Loss of interest",
      "Negative self-talk",
      "Social withdrawal",
    ],
    imageUrl:
      "https://images.unsplash.com/photo-1447752875215-b2761acb3c5d?auto=format&fit=crop&w=1920&q=80",
    themeColor: "bg-[#1E3A8A]",
    heroEyebrow: "Mood Restoration",
    heroDescription:
      "Overcome persistent sadness, fatigue, and loss of interest. Reconnect with joy, vitality, and meaning through evidence-based cognitive and behavioral approaches.",
    seasonsIntro:
      "Low mood rarely arrives all at once. Ongoing stress, loss, isolation, or simply running on empty for too long can gradually dim your sense of interest and energy. Recognising the shift is the first step toward reconnecting with yourself.",
    situations: [
      "I've lost interest in things I used to enjoy",
      "Getting through the day feels exhausting",
      "I'm withdrawing from people I care about",
      "My inner voice has become harsh and critical",
      "I want to feel like myself again",
      "I want support alongside my existing routine",
    ],
    benefits: [
      {
        title: "Activation Strategies",
        description:
          "Re-engage in rewarding activities step-by-step to disrupt the cycle of lethargy.",
      },
      {
        title: "Cognitive Reframing",
        description: "Address negative core beliefs about yourself, the world, and the future.",
      },
      {
        title: "Social Connection",
        description: "Practical plans to rebuild support systems and reduce isolation.",
      },
      {
        title: "Sustainable Momentum",
        description:
          "Build small, achievable steps that compound into lasting change, without pressure to fix everything at once.",
      },
    ],
    benefitsClarifier:
      "A therapist doesn't expect you to 'just think positive.' They help you understand the cycle you're in and build a realistic way out of it.",
    soloStart: {
      question: "What if I don't feel motivated enough to start therapy?",
      answer:
        "That's one of the most common reasons people begin. You don't need energy or motivation to take the first step - your therapist meets you exactly where you are.",
    },
  },
  {
    slug: "sleep-optimization",
    title: "Sleep Optimization",
    badge: "Targeted Care",
    format: "Online Only",
    duration: "50 Mins",
    units: "6 Sessions",
    overview:
      "Rebuild your circadian rhythm and master sleep hygiene. Identify physiological barriers to deep, restorative rest and wake up feeling truly refreshed.",
    recommendedFor: [
      "Insomnia symptoms",
      "Restless sleep",
      "Morning fatigue",
      "Circadian misalignment",
      "Stress-induced waking",
    ],
    imageUrl:
      "https://images.unsplash.com/photo-1541781774459-bb2af2f05b55?auto=format&fit=crop&w=1920&q=80",
    themeColor: "bg-[#1E40AF]",
    heroEyebrow: "Sleep Therapy",
    heroDescription:
      "Rebuild your circadian rhythm and master sleep hygiene. Identify physiological barriers to deep, restorative rest.",
    seasonsIntro:
      "Sleep rarely breaks down for one simple reason. Stress, screen habits, irregular schedules, and racing thoughts at bedtime can all quietly stack up into a cycle that's hard to break alone. Recognising the pattern is the first step toward resetting it.",
    situations: [
      "I lie awake even when I'm exhausted",
      "My mind won't switch off at night",
      "I wake up and can't fall back asleep",
      "I dread bedtime because of how it usually goes",
      "I want to understand why my sleep is so inconsistent",
      "I want a plan, not just more sleep tips",
    ],
    benefits: [
      {
        title: "Circadian Alignment",
        description:
          "Practical light exposure and behavioral routines to reset your biological clock.",
      },
      {
        title: "Stimulus Control",
        description:
          "Re-associate your bed with restful sleep rather than frustration and wakefulness.",
      },
      {
        title: "Cognitive Sleep Therapy",
        description: "Address bedtime anxiety and catastrophic thoughts about sleep loss.",
      },
      {
        title: "A Personalised Routine",
        description:
          "A practical plan built around your actual schedule, habits, and sleep environment.",
      },
    ],
    benefitsClarifier:
      "A sleep therapist doesn't just hand you a checklist. They help you understand the specific pattern keeping you awake and rebuild it step by step.",
    soloStart: {
      question: "Do I need a sleep study before starting?",
      answer:
        "No. Most sleep concerns are addressed through behavioural and cognitive approaches first. Your therapist will guide you toward further evaluation only if it's genuinely needed.",
    },
  },
  {
    slug: "somatic-integration",
    title: "Somatic Integration",
    badge: "Deep Healing",
    format: "In-person Only",
    duration: "75 Mins",
    units: "12 Sessions",
    overview:
      "Release stress and emotional patterns stored in your body. Combine talk therapy with somatic experiencing and mindfulness to achieve complete nervous system alignment.",
    recommendedFor: [
      "Chronic body tension",
      "Somatic stress response",
      "Nervous system regulation",
      "Mind-body disconnect",
      "Emotional blockages",
    ],
    imageUrl:
      "https://images.unsplash.com/photo-1545205597-3d9d02c29597?auto=format&fit=crop&w=1920&q=80",
    themeColor: "bg-[#1D4D74]",
    heroEyebrow: "Somatic Experiencing",
    heroDescription:
      "Release stress and emotional patterns stored in your body. Combine talk therapy with somatic experiencing and mindfulness.",
    seasonsIntro:
      "Stress and emotion don't just live in the mind - they settle in the body as tension, fatigue, and disconnection. Recognising where and how you carry it is the first step toward releasing it for good.",
    situations: [
      "I carry tension in my body that won't release",
      "Talk therapy alone hasn't been enough",
      "I feel disconnected from my body",
      "Stress shows up physically before I notice it emotionally",
      "I want to process feelings I can't fully put into words",
      "I want a more embodied approach to healing",
    ],
    benefits: [
      {
        title: "Nervous System Tracking",
        description:
          "Learn to recognize and track subtle sensations of activation and release in the body.",
      },
      {
        title: "Somatic Release",
        description: "Safely discharge bound survival energy related to stress and trauma.",
      },
      {
        title: "Resilience Cultivation",
        description:
          "Expand your nervous system's capacity to contain and navigate intense emotional states.",
      },
      {
        title: "Mind-Body Integration",
        description:
          "Bring bodily awareness and emotional insight together into one coherent healing process.",
      },
    ],
    benefitsClarifier:
      "A somatic practitioner doesn't just analyse your thoughts. They help you notice and work with what your body is already telling you.",
    soloStart: {
      question: "What if I'm not comfortable focusing on physical sensations at first?",
      answer:
        "That's completely normal, and your practitioner will move at a pace that feels safe. Body awareness is built gradually, never forced.",
    },
  },
];
